/**
 * Dodge afterimages: translucent, cool-tinted copies of a hero's rig frozen at the moment they
 * were spawned, fading out over a few tenths of a second (a short trail of ghosts behind the dash).
 *
 * How: each ghost is a SkinnedMesh per skinned part of the source rig, sharing the part's geometry
 * but bound to a *frozen* skeleton: a Skeleton whose `update()` is a no-op and whose bone matrices
 * are a copy of the source's at spawn time. Bone matrices are world space (bone world × inverse
 * bind), so with the ghost at an identity transform and the source's bind matrix the ghost
 * renders exactly where the hero stood. One shared translucent material per ghost slot (opacity
 * fades), fixed pool, nothing allocated after a source's first dodge.
 */
import * as THREE from 'three';

const SLOTS_PER_SOURCE = 4;
const MAX_SOURCES = 6;

interface Part {
  mesh: THREE.SkinnedMesh;
  src: THREE.SkinnedMesh;
  skel: THREE.Skeleton;
}

interface Ghost {
  group: THREE.Group;
  parts: Part[];
  mat: THREE.MeshBasicMaterial;
  age: number;
  life: number;
  peak: number;
  live: boolean;
}

interface SourceSet {
  source: THREE.Object3D;
  ghosts: Ghost[];
  next: number;
}

const NOOP = (): void => {};

export class AfterImages {
  readonly group = new THREE.Group();
  private readonly sets: SourceSet[] = [];
  private live = 0;
  intensity = 1;

  constructor() {
    this.group.name = 'afterimages';
  }

  /** Spawn a ghost of `source` (a hero rig) now. `color`: linear RGB tint. */
  spawn(source: THREE.Object3D, color: readonly [number, number, number], life = 0.36, peak = 0.26): void {
    if (this.intensity <= 0.05) return;
    const set = this.setFor(source);
    if (!set) return;
    const g = set.ghosts[set.next]!;
    set.next = (set.next + 1) % set.ghosts.length;
    for (const p of g.parts) {
      if (!p.src.visible || !p.src.skeleton) {
        p.mesh.visible = false;
        continue;
      }
      p.mesh.visible = true;
      const from = p.src.skeleton.boneMatrices;
      const to = p.skel.boneMatrices;
      if (from && to && from.length === to.length) {
        to.set(from);
        if (p.skel.boneTexture) p.skel.boneTexture.needsUpdate = true;
      }
    }
    const k = Math.min(1.2, this.intensity);
    g.mat.color.setRGB(color[0] * k, color[1] * k, color[2] * k);
    g.age = 0;
    g.life = life;
    g.peak = peak;
    g.mat.opacity = peak;
    if (!g.live) this.live++;
    g.live = true;
    g.group.visible = true;
  }

  update(dt: number): void {
    if (this.live === 0) return;
    for (const set of this.sets) {
      for (const g of set.ghosts) {
        if (!g.live) continue;
        g.age += dt;
        const t = g.age / g.life;
        if (t >= 1) {
          g.live = false;
          g.group.visible = false;
          this.live--;
          continue;
        }
        g.mat.opacity = g.peak * (1 - t) * (1 - t);
      }
    }
  }

  private setFor(source: THREE.Object3D): SourceSet | null {
    for (const s of this.sets) if (s.source === source) return s;
    if (this.sets.length >= MAX_SOURCES) {
      // Recycle the oldest set (a VRM slot reused by another hero, a new run).
      const old = this.sets.shift()!;
      this.disposeSet(old);
    }
    const srcParts: THREE.SkinnedMesh[] = [];
    source.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (m.isSkinnedMesh && !o.userData['hull'] && m.skeleton && m.geometry) srcParts.push(m);
    });
    if (srcParts.length === 0) return null;
    const ghosts: Ghost[] = [];
    for (let i = 0; i < SLOTS_PER_SOURCE; i++) {
      // Normal blending: stacked layers (body under clothes) get more opaque, never brighter.
      const mat = new THREE.MeshBasicMaterial({ color: 0x6fa8ff, transparent: true, opacity: 0, depthWrite: false, side: THREE.FrontSide, fog: false });
      const group = new THREE.Group();
      group.visible = false;
      const parts: Part[] = [];
      for (const src of srcParts) {
        const skel = new THREE.Skeleton(src.skeleton.bones, src.skeleton.boneInverses);
        skel.update = NOOP; // frozen: keeps the copied matrices
        skel.computeBoneTexture();
        const mesh = new THREE.SkinnedMesh(src.geometry, mat);
        // Attached bind mode at an identity world matrix: bindMatrixInverse stays identity, so
        // the frozen (world-space) bone matrices place the ghost where the hero stood.
        mesh.bind(skel, src.bindMatrix);
        mesh.frustumCulled = false;
        mesh.castShadow = mesh.receiveShadow = false;
        mesh.renderOrder = 7;
        mesh.matrixAutoUpdate = false;
        mesh.userData['noStyle'] = true;
        group.add(mesh);
        parts.push({ mesh, src, skel });
      }
      this.group.add(group);
      ghosts.push({ group, parts, mat, age: 0, life: 1, peak: 0, live: false });
    }
    const set: SourceSet = { source, ghosts, next: 0 };
    this.sets.push(set);
    return set;
  }

  private disposeSet(s: SourceSet): void {
    for (const g of s.ghosts) {
      if (g.live) this.live--;
      for (const p of g.parts) p.skel.dispose();
      g.mat.dispose();
      g.group.removeFromParent();
    }
  }

  clear(): void {
    for (const s of this.sets) for (const g of s.ghosts) {
      if (g.live) this.live--;
      g.live = false;
      g.group.visible = false;
    }
  }

  dispose(): void {
    for (const s of this.sets) this.disposeSet(s);
    this.sets.length = 0;
    this.live = 0;
    this.group.removeFromParent();
  }
}
